import React from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { EVENT_POSITION_FOR_ARCHIVE_ORDERS } from '../../../../../../shop/constants/tracker';
import DifferenceValue from '../../../../components/DifferenceValue';
import OrderNumberLink from '../../../../components/OrderNumberLink';
import { useAppContext } from '../../../../context/appContext';

const ORDER_TYPE_RETURN = 'return';

const TableRenderComponent = ({ id, item }) => {
  const { t } = useTranslation();
  const { slFeatureService } = useAppContext();
  const hasOrderArchiveTestingCrm = slFeatureService.hasFeature(
    'order_archive_testing_crm',
  );
  const getManualPointI18nKey = (orderType) => {
    if (hasOrderArchiveTestingCrm) {
      return 'member_points.fields.revert_member_point.order';
    }
    if (orderType === ORDER_TYPE_RETURN) {
      return 'member_points.fields.revert_member_point.return';
    }
    return 'member_points.fields.revert_member_point';
  };

  const getReturnOrderRevertI18nKey = () => {
    if (hasOrderArchiveTestingCrm) {
      return 'member_points.fields.revert_member_point.order';
    }
    return 'member_points.fields.revert_member_point.return_order_revert';
  };

  const {
    type,
    order_type,
    order_id,
    order_number,
    return_order_id,
    return_order_number,
    remarks,
    redeemed,
    value,
    created_at,
    expire_at,
  } = item;
  switch (id) {
    case 'created_at':
      return dayjs(created_at).format('YYYY/MM/DD h:mma');
    case 'remarks':
      switch (type) {
        case 'manual':
          if (order_id) {
            return (
              <div className="member-center__list-item-remark">
                <span>{t(getManualPointI18nKey(order_type))}</span>
                <OrderNumberLink
                  createdAt={created_at}
                  href={`/orders/${order_id}`}
                  orderId={order_id}
                  orderNumber={order_number}
                  position={
                    EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                  }
                />
              </div>
            );
          } else if (remarks) {
            return <div>{remarks}</div>;
          }
          return null;
        case 'auto_reward':
        case 'reward_campaign_point':
          return (
            <div className="member-center__list-item-remark">
              <span>{t('member_points.fields.auto_reward')}</span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/orders/${order_id}`}
                orderId={order_id}
                orderNumber={order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
              <span>{t('member_points.fields.auto_reward_seconds')}</span>
            </div>
          );
        case 'order_redeemed':
          return (
            <div className="member-center__list-item-remark">
              <span>{t('member_points.fields.order_redeemed')}</span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/orders/${order_id}`}
                orderId={order_id}
                orderNumber={order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
            </div>
          );
        case 'redeemed_to_cash':
          return (
            <div className="member-center__list-item-remark">
              <span>{t('member_points.fields.redeemed_to_cash')}</span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/orders/${order_id}`}
                orderId={order_id}
                orderNumber={order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
            </div>
          );
        case 'order_split_revert':
          return (
            <div className="member-center__list-item-remark">
              <span>
                {t('member_points.fields.revert_member_point.order_split')}
              </span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/orders/${order_id}`}
                orderId={order_id}
                orderNumber={order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
            </div>
          );
        case 'order_edit_revert':
          return (
            <div className="member-center__list-item-remark">
              <span>
                {t('member_points.fields.revert_member_point.order_edit')}
              </span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/orders/${order_id}`}
                orderId={order_id}
                orderNumber={order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
            </div>
          );
        case 'product_review_reward':
          return (
            <div className="member-center__list-item-remark">
              <Trans
                i18nKey="member_points.fields.product_review_reward"
                components={{
                  a: (
                    <OrderNumberLink
                      createdAt={created_at}
                      href={`/orders/${order_id}`}
                      orderId={order_id}
                      orderNumber={order_number}
                      position={
                        EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                      }
                    />
                  ),
                }}
              />
            </div>
          );
        case 'return_order_revert':
          return (
            <div className="member-center__list-item-remark">
              <span>{t(getReturnOrderRevertI18nKey())}</span>
              <OrderNumberLink
                createdAt={created_at}
                href={`/return_orders/${return_order_id}`}
                orderId={return_order_id}
                orderNumber={return_order_number}
                position={
                  EVENT_POSITION_FOR_ARCHIVE_ORDERS.MEMBER_CENTER_POINT_HISTORY
                }
              />
            </div>
          );
        default:
          return <div>{t(`member_points.fields.${type}`)}</div>;
      }
    case 'value':
      return <DifferenceValue isMinus={redeemed} value={value} />;
    case 'expire_at':
      if (redeemed) {
        return '-';
      }
      if (expire_at) {
        return dayjs(expire_at).format('YYYY/MM/DD');
      }
      return t('store_credits.fields.display.never_expired');
    case 'point_balance':
      return item.point_balance;
    default:
      return '';
  }
};

TableRenderComponent.propTypes = {
  id: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
};

export default TableRenderComponent;
